<script setup lang="ts">
/**
 * Ghost-row loader for list views.
 *
 * Renders N card-shaped pulsing placeholders that roughly mirror the
 * height of a DishRow / CategoryRow / TagRow. Used in place of a
 * centred spinner during initial load and pagination — keeps the
 * page's vertical rhythm so jumping to page 2 doesn't collapse and
 * re-expand the layout.
 *
 * Respects prefers-reduced-motion (the global rule in main.css
 * disables the pulse animation there automatically).
 */
interface Props {
  /** How many ghost rows to render. */
  rows?: number
  /**
   * Screen-reader announcement while the skeleton is up. The ghost rows
   * are `aria-hidden` (decorative), so without this an SR user hears
   * nothing during the initial load. Built in here so every list view
   * gets the announcement for free.
   */
  label?: string
}
withDefaults(defineProps<Props>(), { rows: 5, label: 'Loading…' })
</script>

<template>
  <div>
    <p class="sr-only" role="status">{{ label }}</p>
    <ul class="grid gap-2" aria-hidden="true">
      <li
        v-for="i in rows"
        :key="i"
        class="flex items-center gap-3 rounded-lg border border-border bg-card p-3 shadow-sm md:p-4"
      >
        <span class="size-16 shrink-0 animate-pulse rounded-md bg-muted md:size-20" />
        <div class="flex min-w-0 flex-1 flex-col gap-2">
          <span class="h-4 w-2/3 animate-pulse rounded bg-muted" />
          <span class="h-3 w-1/3 animate-pulse rounded bg-muted" />
        </div>
      </li>
    </ul>
  </div>
</template>
